import React, { useState } from 'react';
import { BellRing, FileSignature, FolderInput, PanelsTopLeft, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

const scenes = [
  {
    id: 'pestanas',
    label: 'Pestañas',
    title: 'Varios portales abiertos, cada uno con su certificado.',
    body: 'La pestaña conserva la identidad elegida al abrirla. Puedes cambiar de trámite y volver después sin repetir la selección.',
    image: '/product/certy-multiples-pestanas.webp',
    alt: 'Cuatro portales abiertos en Certy con un certificado distinto en cada pestaña',
    icon: PanelsTopLeft,
    href: '/navegador-varios-certificados-digitales',
  },
  {
    id: 'organiza',
    label: 'Certificados',
    title: 'Encuentra el certificado correcto sin recorrer una lista interminable.',
    body: 'Busca por titular, empresa o emisor. Filtra por estado o favoritos y ordena por nombre o fecha de caducidad.',
    image: '/product/certy-panel-certificados.webp',
    alt: 'Panel de Certy con búsqueda, filtros y una lista de certificados',
    icon: Search,
    href: '/buscar-certificados-digitales-en-windows',
  },
  {
    id: 'importa',
    label: 'Importación',
    title: 'Importa una carpeta y revisa el resultado archivo por archivo.',
    body: 'Certy instala los PFX y P12 compatibles, identifica los que ya estaban y separa los que requieren otra contraseña.',
    image: '/product/certy-importacion-masiva.webp',
    alt: 'Resultado de una importación de certificados en Certy',
    icon: FolderInput,
    href: '/importar-certificados-pfx-p12-masivamente',
  },
  {
    id: 'firma',
    label: 'Firma PDF',
    title: 'Firma un PDF completo desde una única vista.',
    body: 'Elige el certificado, coloca el sello y aplica la firma a todas las páginas del documento.',
    image: '/product/certy-firma-pdf.webp',
    alt: 'Vista de Certy para firmar un PDF de varias páginas',
    icon: FileSignature,
    href: '/firma-pdf-con-certificado-digital',
  },
  {
    id: 'avisa',
    label: 'Avisos',
    title: 'Recibe un aviso antes de la caducidad.',
    body: 'Configura recordatorios por correo y elige con cuánta antelación quieres recibirlos.',
    image: '/product/certy-recordatorios.webp',
    alt: 'Configuración de avisos de caducidad en Certy',
    icon: BellRing,
    href: '/control-caducidad-certificados-digitales',
  },
];

const Features: React.FC = () => {
  const [activeScene, setActiveScene] = useState(0);
  const scene = scenes[activeScene];

  return (
    <section id="features" className="border-b border-[#c9d2cc] bg-white py-14 md:py-16" aria-labelledby="producto-en-accion">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-10">
        <div className="border-b border-[#c9d2cc] pb-8">
          <h2 id="producto-en-accion" className="max-w-[17ch] scroll-mt-28 text-[clamp(2.25rem,3.7vw,4rem)] font-semibold leading-[1] tracking-[-0.05em] text-[#07110d]">
            Qué puedes hacer con Certy.
          </h2>
          <p className="mt-4 max-w-[58ch] text-base leading-7 text-[#526158]">
            Selecciona una función para verla en la aplicación.
          </p>
        </div>

        <div className="mt-8 overflow-x-auto">
          <div className="flex min-w-max gap-2" role="tablist" aria-label="Funciones de Certy">
            {scenes.map((item, index) => {
              const Icon = item.icon;
              const active = index === activeScene;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveScene(index)}
                  className={`inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-sm font-semibold transition-colors ${
                    active
                      ? 'border-[#07110d] bg-[#07110d] text-white'
                      : 'border-[#c9d2cc] bg-white text-[#34463b] hover:border-[#15783a] hover:text-[#07110d]'
                  }`}
                >
                  <Icon className={`h-4 w-4 ${active ? 'text-[#85f6aa]' : 'text-[#15783a]'}`} />
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-xl border border-[#c9d2cc] bg-[#f4f6f4]">
          <div className="min-w-0 p-2 sm:p-3">
            <div className="aspect-[8/5] overflow-hidden rounded-lg border border-[#c9d2cc] bg-white shadow-[0_18px_50px_rgba(7,17,13,0.1)]">
              <img
                key={scene.image}
                src={scene.image}
                alt={scene.alt}
                width="2880"
                height="1800"
                loading="lazy"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
          <article className="grid gap-5 border-t border-[#c9d2cc] bg-white p-7 md:grid-cols-[0.9fr_1.1fr] md:items-start md:p-9">
            <h3 className="max-w-[20ch] text-3xl font-semibold leading-[1.04] tracking-[-0.045em] text-[#07110d] md:text-4xl">
              {scene.title}
            </h3>
            <div className="md:pt-1"><p className="max-w-[48ch] text-base leading-7 text-[#526158]">{scene.body}</p><Link to={scene.href} className="mt-5 inline-flex text-sm font-semibold text-[#15783a] underline underline-offset-4">Ver cómo funciona</Link></div>
          </article>
        </div>

      </div>
    </section>
  );
};

export default Features;
